import "../insTimeSeriesChart/insTimeSeriesChart.scss";
import Highcharts from "highcharts";
import hcMore from "highcharts/highcharts-more";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useDispatch } from "react-redux";
import { yAxisFormatter2 } from "../insHorizontalBarChart/ins-horizontal-bar-chart-formatter";
import { InsHighcharts } from "components/widgets/base";
import { InsLabelValueTooltip } from "components/widgets/base/common/insLabelValueTooltip";
import { InsQueryStatedElement } from "components/widgets/base/insQueryStatedElement";
import { useCurrentWidgetState, useWidgetComponentProps } from "components/widgets/hooks/use-current-widget";
import { useGetDatapointsWidgetData } from "hooks/widget-data/use-datapoints";
import { setCurrentStatusMasterDetails } from "store/current-dashboard";
import { CommmonFormatEnum } from "datapoints";
import { InsStackedColumnChartSubtitle } from "./InsStackedColumnChartSubtitle";
import { mapValueFormatToCommonFormat, useGetDateText } from "../base/utils";
hcMore(Highcharts);
const addSubtitle = (options, subtitle, processedSubtitle) => {
    const subtitleToUse = processedSubtitle ?? subtitle;
    if (subtitleToUse) {
        options.subtitle = {
            align: "left",
            text: renderToStaticMarkup(React.createElement(InsStackedColumnChartSubtitle, { subtitle: subtitleToUse })),
            useHTML: true,
            verticalAlign: "bottom",
            x: -8,
            y: 24
        };
    }
};
export const InsStackedColumnChart = (props) => {
    const { sectionId, widgetId } = props, widgetProps = useWidgetComponentProps(sectionId, widgetId), [[dataPointData], queryState] = useGetDatapointsWidgetData(props);
    const widgetState = useCurrentWidgetState(sectionId, widgetId);
    const selectedValues = widgetState?.selectedValues ?? [];
    const selectedDropDownProps = Object.entries(selectedValues).map(([key, value]) => widgetProps.dataSelectProps[parseInt(key, 10)].selectValues[value]);
    const dispatch = useDispatch();
    /*
     * Process subtitle date dynamically before SSR
     * Replace [dateValue] placeholder with dynamic date
     */
    const dynamicDate = useGetDateText("[dateValue]");
    const processedSubtitle = widgetProps.subtitle?.replace(/\[dateValue\]/gu, dynamicDate || "");
    const options = {
        chart: {
            alignThresholds: true,
            className: "ins-time-series-chart ins-stacked-column-chart",
            colorCount: widgetProps.colorCount ?? 10,
            styledMode: true
        },
        credits: { enabled: false },
        legend: { align: "left", enabled: widgetProps.showLegend !== false, maxHeight: 85, x: 28 },
        plotOptions: {
            column: { animation: false, stacking: widgetProps?.unstack ? undefined : "normal", stickyTracking: false, zIndex: 1 },
            columnrange: {
                grouping: false
            },
            line: { zIndex: 2 },
            scatter: { zIndex: 3 }
        },
        series: dataPointData?.data,
        title: { text: "" },
        tooltip: {
            formatter() {
                // With shared tooltip, use first point's tooltip data (all points at same x have same tooltip)
                const tooltipData = this.points?.[0]?.point?.options?.custom?.tooltip ?? this.point?.options?.custom?.tooltip;
                return renderToStaticMarkup(React.createElement(InsLabelValueTooltip, { labelValuePairs: tooltipData }));
            },
            shared: dataPointData?.additionalData?.hasTooltipKeys ?? false,
            useHTML: true
        },
        xAxis: {
            categories: dataPointData?.additionalData?.xaxis,
            title: { text: dataPointData?.additionalData?.xaxisTitle },
            type: "category"
        },
        yAxis: dataPointData?.additionalData?.yaxis?.map((x) => ({
            alignTicks: true,
            allowDecimals: x.format !== CommmonFormatEnum.value,
            gridLineWidth: 2,
            labels: {
                enabled: true,
                formatter() {
                    const format = x.format ?? (selectedDropDownProps.length ? mapValueFormatToCommonFormat(selectedDropDownProps[0]?.valueFormat) : undefined);
                    return yAxisFormatter2(this, false, undefined, format);
                },
                useHTML: true
            },
            max: x.max ?? null,
            min: x.min ?? null,
            // Do not set minRange here — it distorts axes when values are small decimals (e.g. percentages)
            opposite: x.opposite,
            plotLines: [{ value: 0 }],
            title: { text: x.name },
            visible: x.visible ?? true
        }))
    };
    addSubtitle(options, widgetProps.subtitle, processedSubtitle);
    if (options.series) {
        for (const item of options.series) {
            delete (item.events || {}).legendItemClick;
            if (widgetProps.childrenId) {
                item.events = { ...item.events,
                    click(event) {
                        dispatch(setCurrentStatusMasterDetails({
                            childrenId: widgetProps.childrenId,
                            sectionId,
                            selectedValue: event.point?.options?.custom?.id,
                            widgetId
                        }));
                    } };
            }
        }
    }
    return React.createElement(InsQueryStatedElement, { widgetFactoryProps: props, queryState: queryState, hasData: dataPointData?.data?.length > 0 && dataPointData?.data?.[0]?.data?.length > 0 },
        React.createElement(InsHighcharts, { colorSet: widgetProps.colorSet, options: options, ...props }));
};
